import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Avatar, Badge, Popover } from 'antd'
import { selectIsNotificationsPopoverOpened, selectNotifications } from 'features/common/model/selectors/notificationsSelectors'
import { fetchNotifications, setIsNotificationsPopoverOpened, subscribeNotificationsToWSThunk, unubscribeNotificationsToWSThunk } from 'features/common/model/slice/notificationsSlice'
import { userIdSelector } from 'features/user/model/selectors/userSelectors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { BellOutlined } from 'shared/ui'
import styled, { createGlobalStyle } from 'styled-components'

import { familyMembersWithAvatarsSelector } from '../../../team/model/selectors/teamSelectors'
import { AI_BOT_USER_ID } from '../../../../shared/constants/ai'

import { NotificationsPanel } from './NotificationsPanel'

const GlobalStyle = createGlobalStyle`
  .custom-notification {
    .ant-notification-notice-message {
      display: none;
    }
    .ant-notification-notice-description {
      margin-left: 0;
      margin-right: 0;
    }
    .ant-notification-notice-close {
      top: 16px;
      right: 16px;
    }
  }
`

interface NotificationIconProps {
  className?: string
}

export const NotificationIcon: React.FC<NotificationIconProps> = ({ className = '' }) => {
  const userId = useAppSelector(userIdSelector)
  const notifications = useAppSelector(selectNotifications)
  const isNotificationsPopoverOpened = useAppSelector(selectIsNotificationsPopoverOpened)
  const familyMembers = useAppSelector(familyMembersWithAvatarsSelector)
  const familyMembersRef = useRef<typeof familyMembers>(null)

  useEffect(() => {
    familyMembersRef.current = familyMembers
  }, [familyMembers])

  const storageKey = `notifications:last_seen_unread_created:${userId || 'anonymous'}`

  const latestUnreadCreatedMs = useMemo(() => {
    const unread = notifications.filter((n) => !n.is_viewed)
    if (unread.length === 0) return null
    return unread
      .map((n) => new Date(n.created).getTime())
      .reduce((max, cur) => (cur > max ? cur : max), 0)
  }, [notifications])

  const [lastSeenUnreadCreatedMs, setLastSeenUnreadCreatedMs] = useState<number>(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null
      const parsed = raw ? Number(raw) : 0
      return Number.isFinite(parsed) ? parsed : 0
    } catch {
      return 0
    }
  })

  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null
      const parsed = raw ? Number(raw) : 0
      setLastSeenUnreadCreatedMs(Number.isFinite(parsed) ? parsed : 0)
    } catch {
      setLastSeenUnreadCreatedMs(0)
    }
  }, [storageKey])

  const hasUnread = !!latestUnreadCreatedMs && latestUnreadCreatedMs > lastSeenUnreadCreatedMs

  const dispatch = useAppDispatch()

  const getMemberAvatarUrl = useCallback((memberId) => {
    const member = familyMembersRef.current?.find((member) => member.user_id === memberId)
    return member?.avatarUrl
  }, [])

  // it's hack for now
  // because initiator_id of a notification of the ai bot is 0 but author_id of a comment is -1
  const getMemberId = (notification) => notification.initiator_id === 0 ? AI_BOT_USER_ID : notification.initiator_id

  useEffect(() => {
    const generateNotificationContent = (notification) => {
      return (
        <SNotificationContent>
          <SAvatarContainer>
            <SAvatar
              $hasAvatar={!!getMemberAvatarUrl(getMemberId(notification))}
              src={getMemberAvatarUrl(getMemberId(notification)) || undefined}
            >
              {notification.initiator_name.charAt(0)}

              <STypeIconContainer>
                {
                  notification.type === 'mention_in_comment' && <STypeImg src="/icons/mentioned.svg" />
                }

                {
                  notification.type === 'new_comment' && <STypeImg src="/icons/commented.svg" />
                }

                {
                  notification.type === 'reply_comment' && <STypeImg src="/icons/reply.svg" />
                }
              </STypeIconContainer>
            </SAvatar>
          </SAvatarContainer>

          <STextContent>
            <SAuthorName>
              {notification.initiator_name}
            </SAuthorName>

            <SInfoRow>
              {
                notification.type === 'mention_in_comment' && 'Mentioned you'
              }

              {
                notification.type === 'reply_comment' && 'Reply'
              }
            </SInfoRow>

            <SMessageText>
              {notification.messages[0].text}
            </SMessageText>
          </STextContent>
        </SNotificationContent>
      )
    }

    dispatch(subscribeNotificationsToWSThunk({ generateNotificationContent }))

    return () => {
      dispatch(unubscribeNotificationsToWSThunk())
    }
  }, [getMemberAvatarUrl, dispatch])

  useEffect(() => {
    if (userId) {
      dispatch(fetchNotifications())

      const interval = setInterval(() => {
        dispatch(fetchNotifications())
      }, 5 * 60 * 1000)

      return () => clearInterval(interval)
    }
  }, [userId, dispatch])

  const handleOpenChange = (newOpen: boolean) => {
    dispatch(setIsNotificationsPopoverOpened(newOpen))

    if (newOpen) {
      const baseline = latestUnreadCreatedMs ?? Date.now()
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(storageKey, String(baseline))
        }
      } catch {
      }
      setLastSeenUnreadCreatedMs(baseline)
    }
  }

  useEffect(() => {
    if (isNotificationsPopoverOpened) {
      const baseline = latestUnreadCreatedMs ?? Date.now()
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(storageKey, String(baseline))
        }
      } catch {
      }
      setLastSeenUnreadCreatedMs(baseline)
    }
  }, [isNotificationsPopoverOpened, latestUnreadCreatedMs, storageKey])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) {
        const parsed = e.newValue ? Number(e.newValue) : 0
        if (Number.isFinite(parsed)) {
          setLastSeenUnreadCreatedMs(parsed)
        }
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [storageKey])

  const handleClose = () => {
    dispatch(setIsNotificationsPopoverOpened(false))
  }

  return (
    <SNotificationWrapper className={className}>
      <GlobalStyle />

      <Popover
        content={<NotificationsPanel onClose={handleClose} />}
        trigger="click"
        open={isNotificationsPopoverOpened}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
        overlayStyle={{ width: 630, padding: 0 }}
        overlayInnerStyle={{ padding: 0 }}
        arrow={false}
      >
        <SBadge dot={hasUnread}
          status="error"
        >
          <SBellIcon />
        </SBadge>
      </Popover>
    </SNotificationWrapper>
  )
}

const SBellIcon = styled(BellOutlined)`
  font-size: 20px;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: var(--color-primary);
  }
`

const SNotificationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const SBadge = styled(Badge)`
  .ant-badge-dot {
    width: 6px;
    height: 6px;
    right: 4px;
    top: 4px;
  }

  background-color: #0000000F;
  cursor: pointer;
  min-width: 32px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #000000E0;
`

const SNotificationContent = styled.div`
  display: flex;
`

const SAvatarContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-right: 16px;
`

const SAvatar = styled(Avatar)<{ $hasAvatar?: boolean }>`
  min-width: 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.$hasAvatar ? 'transparent' : '#00000040'};
  font-size: 14px;
  color: #FFFFFF;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  position: relative;
`

const STypeIconContainer = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  border-radius: 50%;
  position: absolute;
  right: -7px;
  bottom: -6px;
`

const STypeImg = styled.img`
  width: 14px;
  height: 14px;
`

const STextContent = styled.div`
  flex: 1;
`

const SAuthorName = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #000000E0;
`

const SInfoRow = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 400;
  color: #000000E0;
`

const SMessageText = styled.div`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000A6;
  word-break: break-all;
`
